/**
 * Modu — Drawer Profiles client (Tier 2)
 *
 * Sibling of md-favourites.js. Wraps the Cloudflare Worker endpoints for the
 * physical-drawer catalogue:
 *
 *   POST /apps/vision-extract/save-drawer    → upsert
 *   POST /apps/vision-extract/get-drawers    → list
 *   POST /apps/vision-extract/delete-drawer  → remove + cascade-unlink builds
 *   POST /apps/vision-extract/photo/upload   → R2 photo upload (with x-md-customer-id header)
 *   POST /apps/vision-extract/photo/delete   → R2 photo delete
 *   GET  /apps/vision-extract/photo/get/:key → R2 photo stream
 *
 * Photos are resized client-side to 1200 px wide JPEG before upload (max
 * ~150 KB) so the worker doesn't have to do image transformation. Reads are
 * always proxied through the worker — never expose R2 endpoints directly.
 *
 * Public API (window.MD_DrawerProfiles):
 *   listAsync(cb)            cb(profiles[])
 *   getCachedMap()           returns id -> profile dict (sync, may be empty)
 *   save(profile, cb)        upsert (profile.id present → edit)
 *   remove(id, cb)           delete + cascade
 *   uploadPhoto(file, cb)    cb(photoKey | null)
 *   deletePhoto(key, cb)
 *   photoUrl(key)            returns the worker-fronted URL
 */
(function () {
  'use strict';

  var API_ENDPOINT = '/apps/vision-extract';
  var CACHE_TTL_MS = 60 * 1000;

  var _cache = null;
  var _cacheTime = 0;
  var _inFlightList = null;

  function mdT(key, variables) {
    if (window.FoxTheme && FoxTheme.i18n && typeof FoxTheme.i18n.t === 'function') {
      return FoxTheme.i18n.t(key, variables);
    }
    return key;
  }

  function builderUrl() {
    var root = (window.FoxTheme && FoxTheme.i18n && FoxTheme.i18n.rootUrl) || '/';
    return (window.MD_ACCOUNT && window.MD_ACCOUNT.builderUrl) || root + 'pages/drawer-builder';
  }

  function getCustomerId() {
    var ctx = window.MD_FAV || {};
    var id = ctx.customerId;
    if (!id || id === 0 || id === '0') return null;
    return String(id);
  }

  function postJSON(path, body) {
    return fetch(API_ENDPOINT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) { return r.json(); });
  }

  function listAsync(cb) {
    cb = cb || function () {};
    var customerId = getCustomerId();
    if (!customerId) return cb([]);
    if (_cache && Date.now() - _cacheTime < CACHE_TTL_MS) return cb(_cache);
    if (_inFlightList) return _inFlightList.then(cb);
    _inFlightList = postJSON('/get-drawers', { customerId: customerId })
      .then(function (result) {
        _inFlightList = null;
        var list = (result && result.ok && Array.isArray(result.drawers)) ? result.drawers : [];
        _cache = list;
        _cacheTime = Date.now();
        return list;
      })
      .catch(function (e) {
        _inFlightList = null;
        console.error('[MD_DrawerProfiles] list failed:', e);
        return [];
      });
    _inFlightList.then(cb);
  }

  function getCachedMap() {
    if (!_cache) return {};
    var map = {};
    _cache.forEach(function (d) { map[d.id] = d; });
    return map;
  }

  function invalidateCache() {
    _cache = null;
    _cacheTime = 0;
    try { window.dispatchEvent(new CustomEvent('md:drawers-updated')); } catch (_) {}
  }

  function save(profile, cb) {
    cb = cb || function () {};
    var customerId = getCustomerId();
    if (!customerId) return cb({ ok: false, error: 'Login required' });
    var payload = {
      customerId: customerId,
      drawer: {
        name: profile.name,
        room: profile.room,
        lengthMM: profile.lengthMM,
        widthMM: profile.widthMM,
        heightMM: profile.heightMM,
        notes: profile.notes,
        photoKey: profile.photoKey || ''
      }
    };
    if (profile.id) payload.drawerId = profile.id;
    postJSON('/save-drawer', payload)
      .then(function (result) {
        if (result && result.ok) invalidateCache();
        cb(result);
      })
      .catch(function (e) { cb({ ok: false, error: String(e) }); });
  }

  function remove(id, cb) {
    cb = cb || function () {};
    var customerId = getCustomerId();
    if (!customerId) return cb({ ok: false, error: 'Login required' });
    postJSON('/delete-drawer', { customerId: customerId, drawerId: id })
      .then(function (result) {
        if (result && result.ok) invalidateCache();
        cb(result);
      })
      .catch(function (e) { cb({ ok: false, error: String(e) }); });
  }

  // -------------------------------------------------------------------------
  // Client-side image resize before upload
  // -------------------------------------------------------------------------

  function resizeImageFile(file, maxW, quality) {
    return new Promise(function (resolve, reject) {
      if (!file || !/^image\//.test(file.type)) {
        return reject(new Error('Not an image'));
      }
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        try {
          var ratio = Math.min(1, maxW / img.naturalWidth);
          var w = Math.round(img.naturalWidth * ratio);
          var h = Math.round(img.naturalHeight * ratio);
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(function (blob) {
            URL.revokeObjectURL(url);
            if (!blob) return reject(new Error('toBlob failed'));
            resolve(blob);
          }, 'image/jpeg', quality || 0.85);
        } catch (e) { reject(e); }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('Image load failed'));
      };
      img.src = url;
    });
  }

  function uploadPhoto(file, cb) {
    cb = cb || function () {};
    var customerId = getCustomerId();
    if (!customerId) return cb(null, new Error('Login required'));

    resizeImageFile(file, 1200, 0.85)
      .then(function (blob) {
        return fetch(API_ENDPOINT + '/photo/upload', {
          method: 'POST',
          headers: {
            'Content-Type': 'image/jpeg',
            'x-md-customer-id': customerId
          },
          body: blob
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (result) {
        if (result && result.ok && result.photoKey) cb(result.photoKey, null);
        else cb(null, new Error((result && result.error) || 'Upload failed'));
      })
      .catch(function (e) { cb(null, e); });
  }

  function deletePhoto(key, cb) {
    cb = cb || function () {};
    var customerId = getCustomerId();
    if (!customerId) return cb({ ok: false, error: 'Login required' });
    postJSON('/photo/delete', { customerId: customerId, photoKey: key })
      .then(cb)
      .catch(function (e) { cb({ ok: false, error: String(e) }); });
  }

  function photoUrl(key) {
    if (!key) return '';
    return API_ENDPOINT + '/photo/get/' + encodeURIComponent(key);
  }

  // -------------------------------------------------------------------------
  // Account "My Drawers" tab rendering
  // -------------------------------------------------------------------------

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function buildsForProfile(profileId, builds) {
    if (!profileId || !Array.isArray(builds)) return [];
    return builds.filter(function (b) { return b.drawerProfileId === profileId; });
  }

  function renderDrawerCard(d, latestBuild) {
    var photoSrc = d.photoKey ? photoUrl(d.photoKey) : '';
    var photoHtml = photoSrc
      ? '<img class="md-drawer-card__photo" src="' + escapeHtml(photoSrc) + '" alt="" loading="lazy">'
      : '<div class="md-drawer-card__photo md-drawer-card__photo--placeholder">' + escapeHtml(mdT('modu.account_runtime.no_photo')) + '</div>';
    // Drawer ID rides in a query param so it doesn't collide with the
    // builder's hash parser (which only knows about variantId-h/w/t/l/r tokens
    // and the leading `LxW`).
    var deepLink = builderUrl() + '?drawer=' + encodeURIComponent(d.id) +
      '#' + (d.lengthMM || 0) + 'x' + (d.widthMM || 0);
    var meta = [];
    meta.push(escapeHtml((d.lengthMM || 0) + ' × ' + (d.widthMM || 0) + ' mm'));
    if (d.heightMM) meta.push(escapeHtml(mdT('modu.account_runtime.depth', { depth: d.heightMM })));
    meta.push(escapeHtml(d.room || mdT('customer.modu.drawers.card_no_room')));
    var reorderHtml = latestBuild
      ? '<a class="btn btn--underline btn--small" href="' +
          escapeHtml(latestBuild.pathname || builderUrl()) +
          escapeHtml(latestBuild.hash || '') + '">' + escapeHtml(mdT('customer.modu.drawers.reorder')) + '</a>'
      : '';
    return '<article class="md-drawer-card" data-md-drawer-card data-drawer-id="' + escapeHtml(d.id) + '">' +
      photoHtml +
      '<div class="md-drawer-card__body">' +
        '<h3 class="md-drawer-card__title">' + escapeHtml(d.name) + '</h3>' +
        '<p class="md-drawer-card__meta">' + meta.join(' · ') + '</p>' +
        (d.notes ? '<p class="md-drawer-card__meta">' + escapeHtml(d.notes) + '</p>' : '') +
        '<div class="md-drawer-card__actions">' +
          '<a class="btn btn--primary btn--small" href="' + escapeHtml(deepLink) + '">' + escapeHtml(mdT('customer.modu.drawers.design_for')) + '</a>' +
          reorderHtml +
          '<button type="button" class="btn btn--underline btn--small" data-md-drawer-edit="' + escapeHtml(d.id) + '">' + escapeHtml(mdT('customer.modu.drawers.edit')) + '</button>' +
          '<button type="button" class="btn btn--underline btn--small" data-md-drawer-delete="' + escapeHtml(d.id) + '">' + escapeHtml(mdT('customer.modu.drawers.delete')) + '</button>' +
        '</div>' +
      '</div>' +
    '</article>';
  }

  function renderForm(container, existing, onSubmit, onCancel) {
    existing = existing || {};
    var html =
      '<form class="md-drawer-form" data-md-drawer-form>' +
        '<div class="md-drawer-form__field">' +
          '<label for="md-d-name">' + escapeHtml(mdT('customer.modu.drawers.form_name')) + '</label>' +
          '<input type="text" id="md-d-name" name="name" required maxlength="80" placeholder="' + escapeHtml(mdT('customer.modu.drawers.form_name_placeholder')) + '" value="' + escapeHtml(existing.name || '') + '">' +
        '</div>' +
        '<div class="md-drawer-form__row">' +
          '<div class="md-drawer-form__field">' +
            '<label for="md-d-room">' + escapeHtml(mdT('customer.modu.drawers.form_room')) + '</label>' +
            '<input type="text" id="md-d-room" name="room" maxlength="60" placeholder="' + escapeHtml(mdT('customer.modu.drawers.form_room_placeholder')) + '" value="' + escapeHtml(existing.room || '') + '">' +
          '</div>' +
          '<div class="md-drawer-form__field">' +
            '<label for="md-d-length">' + escapeHtml(mdT('customer.modu.drawers.form_length')) + '</label>' +
            '<input type="number" id="md-d-length" name="lengthMM" required min="50" max="2000" value="' + escapeHtml(existing.lengthMM || '') + '">' +
          '</div>' +
          '<div class="md-drawer-form__field">' +
            '<label for="md-d-width">' + escapeHtml(mdT('customer.modu.drawers.form_width')) + '</label>' +
            '<input type="number" id="md-d-width" name="widthMM" required min="50" max="2000" value="' + escapeHtml(existing.widthMM || '') + '">' +
          '</div>' +
          '<div class="md-drawer-form__field">' +
            '<label for="md-d-height">' + escapeHtml(mdT('customer.modu.drawers.form_height')) + '</label>' +
            '<input type="number" id="md-d-height" name="heightMM" min="0" max="500" value="' + escapeHtml(existing.heightMM || '') + '">' +
          '</div>' +
        '</div>' +
        '<div class="md-drawer-form__field">' +
          '<label for="md-d-notes">' + escapeHtml(mdT('customer.modu.drawers.form_notes')) + '</label>' +
          '<textarea id="md-d-notes" name="notes" rows="2" maxlength="280" placeholder="' + escapeHtml(mdT('customer.modu.drawers.form_notes_placeholder')) + '">' + escapeHtml(existing.notes || '') + '</textarea>' +
        '</div>' +
        '<div class="md-drawer-form__photo">' +
          '<label for="md-d-photo">' + escapeHtml(mdT('customer.modu.drawers.form_photo')) + '</label>' +
          (existing.photoKey
            ? '<img src="' + escapeHtml(photoUrl(existing.photoKey)) + '" alt="">'
            : '') +
          '<input type="hidden" name="photoKey" value="' + escapeHtml(existing.photoKey || '') + '">' +
          '<input type="file" id="md-d-photo" accept="image/*" data-md-drawer-photo-input>' +
          (existing.photoKey
            ? '<button type="button" class="btn btn--underline btn--small" data-md-drawer-photo-remove>' + escapeHtml(mdT('customer.modu.drawers.form_photo_remove')) + '</button>'
            : '') +
          '<span data-md-drawer-photo-status class="text-sm" style="color:#757571"></span>' +
        '</div>' +
        '<div class="md-drawer-form__actions">' +
          '<button type="button" class="btn btn--underline" data-md-drawer-cancel>' + escapeHtml(mdT('customer.modu.drawers.form_cancel')) + '</button>' +
          '<button type="submit" class="btn btn--primary">' + escapeHtml(mdT('customer.modu.drawers.form_save')) + '</button>' +
        '</div>' +
      '</form>';
    container.innerHTML = html;

    var form = container.querySelector('[data-md-drawer-form]');
    var fileInput = container.querySelector('[data-md-drawer-photo-input]');
    var photoKeyInput = form.querySelector('input[name="photoKey"]');
    var status = container.querySelector('[data-md-drawer-photo-status]');
    var img = container.querySelector('.md-drawer-form__photo img');

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file) return;
        if (status) status.textContent = mdT('modu.account_runtime.uploading');
        uploadPhoto(file, function (key, err) {
          if (err || !key) {
            if (status) status.textContent = mdT('modu.account_runtime.upload_failed');
            return;
          }
          // Best-effort: delete previous photo so we don't leak orphans.
          var prev = photoKeyInput.value;
          if (prev && prev !== key) deletePhoto(prev, function () {});
          photoKeyInput.value = key;
          if (status) status.textContent = mdT('modu.account_runtime.photo_updated');
          if (img) img.src = photoUrl(key);
          else {
            var newImg = document.createElement('img');
            newImg.src = photoUrl(key);
            newImg.alt = '';
            container.querySelector('.md-drawer-form__photo').prepend(newImg);
          }
        });
      });
    }

    var removeBtn = container.querySelector('[data-md-drawer-photo-remove]');
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        var prev = photoKeyInput.value;
        if (prev) deletePhoto(prev, function () {});
        photoKeyInput.value = '';
        if (img) img.remove();
        removeBtn.remove();
      });
    }

    var cancelBtn = container.querySelector('[data-md-drawer-cancel]');
    if (cancelBtn) cancelBtn.addEventListener('click', onCancel);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var fd = new FormData(form);
      var profile = {
        id: existing.id || null,
        name: fd.get('name'),
        room: fd.get('room'),
        lengthMM: parseFloat(fd.get('lengthMM')) || 0,
        widthMM: parseFloat(fd.get('widthMM')) || 0,
        heightMM: parseFloat(fd.get('heightMM')) || 0,
        notes: fd.get('notes'),
        photoKey: fd.get('photoKey') || ''
      };
      onSubmit(profile);
    });
  }

  function renderDrawersInto(container) {
    if (!container) return;
    if (!getCustomerId()) {
      // window.MD_ACCOUNT.loginUrl = routes.storefront_login_url (returns here after login).
      var loginHref = (window.MD_ACCOUNT && window.MD_ACCOUNT.loginUrl) || '/account/login';
      var loginLink = '<a href="' + escapeHtml(loginHref) + '">' + escapeHtml(mdT('modu.account_runtime.log_in')) + '</a>';
      container.innerHTML =
        '<div class="md-drawers__empty"><p>' + mdT('modu.account_runtime.login_manage_html', { login_link: loginLink }) + '</p></div>';
      return;
    }

    container.innerHTML = '<div class="md-drawers__empty"><p>' + escapeHtml(mdT('modu.account_runtime.loading_drawers')) + '</p></div>';

    listAsync(function (drawers) {
      var headerHtml =
        '<div class="md-drawers__header">' +
          '<div>' +
            '<h2 class="md-drawers__title h3">' + escapeHtml(mdT('customer.modu.drawers.title')) + '</h2>' +
            '<p class="md-drawers__subtitle">' + escapeHtml(mdT('customer.modu.drawers.subtitle')) + '</p>' +
          '</div>' +
          '<button type="button" class="btn btn--primary" data-md-drawer-add>' + escapeHtml(mdT('customer.modu.drawers.add')) + '</button>' +
        '</div>' +
        '<div data-md-drawer-form-host></div>';

      if (!drawers.length) {
        container.innerHTML = headerHtml +
          '<div class="md-drawers__empty">' +
            '<p><strong>' + escapeHtml(mdT('customer.modu.drawers.empty_title')) + '</strong></p>' +
            '<p>' + escapeHtml(mdT('customer.modu.drawers.empty_subtitle')) + '</p>' +
          '</div>';
        bindDrawerListEvents(container);
        return;
      }

      // Pull builds so each drawer card can show "Reorder last design".
      var builds = [];
      try {
        if (window.MD_Favourites && typeof window.MD_Favourites.listBuilds === 'function') {
          builds = window.MD_Favourites.listBuilds() || [];
        }
      } catch (_) {}

      var cards = drawers.map(function (d) {
        var linked = buildsForProfile(d.id, builds);
        var latest = linked.length ? linked[0] : null; // builds list is newest-first
        return renderDrawerCard(d, latest);
      }).join('');

      container.innerHTML = headerHtml + '<div class="md-drawers__grid">' + cards + '</div>';
      bindDrawerListEvents(container);
    });
  }

  function bindDrawerListEvents(container) {
    if (container.dataset.mdBound) return;
    container.dataset.mdBound = '1';
    container.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-md-drawer-add]');
      if (addBtn) {
        var host = container.querySelector('[data-md-drawer-form-host]');
        renderForm(host, {}, handleSubmit, function () { host.innerHTML = ''; });
        host.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      var editBtn = e.target.closest('[data-md-drawer-edit]');
      if (editBtn) {
        var id = editBtn.getAttribute('data-md-drawer-edit');
        var existing = (_cache || []).find(function (d) { return d.id === id; });
        if (!existing) return;
        var host = container.querySelector('[data-md-drawer-form-host]');
        renderForm(host, existing, handleSubmit, function () { host.innerHTML = ''; });
        host.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      var delBtn = e.target.closest('[data-md-drawer-delete]');
      if (delBtn) {
        var did = delBtn.getAttribute('data-md-drawer-delete');
        if (!window.confirm(mdT('customer.modu.drawers.delete_confirm'))) return;
        remove(did, function (result) {
          if (result && result.ok) renderDrawersInto(container);
          else alert((result && result.error) || mdT('modu.account_runtime.could_not_delete'));
        });
      }
    });

    function handleSubmit(profile) {
      save(profile, function (result) {
        if (result && result.ok) {
          renderDrawersInto(container);
        } else {
          alert((result && result.error) || mdT('modu.account_runtime.could_not_save'));
        }
      });
    }
  }

  // Note: the in-builder "For drawer" pill was removed — customers pick a saved
  // drawer from their profile (My Drawers → "Design for this drawer") instead.
  // The save dialog still offers an optional per-save drawer link.

  window.MD_DrawerProfiles = {
    listAsync: listAsync,
    getCachedMap: getCachedMap,
    save: save,
    remove: remove,
    uploadPhoto: uploadPhoto,
    deletePhoto: deletePhoto,
    photoUrl: photoUrl,
    invalidateCache: invalidateCache,
    renderDrawersInto: renderDrawersInto
  };

  // Pre-warm the cache for logged-in customers so the saved-builds card grid
  // and the builder profile picker can render synchronously on first paint.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { listAsync(function () {}); });
  } else {
    listAsync(function () {});
  }
})();
